import { FormAncestryRootState, selectAncestorInstances } from '@app-store'
import { store } from '../utils/agent.store'
import { getFormAncestryGetValues } from './formAncestry.registry'
import { getRuleCache } from './formBehaviorRuleCache.registry'
import { resolveFieldRef } from './formBehavior.utils'
import { getValueByDotPath } from './formRuleCachePath.utils'
import {
  AncestorCacheRef,
  AncestorFieldRef,
  FormAncestryRuntimeEntry,
} from '../types/formBehavior.types'

export const resolveFormAncestors = (
  formInstanceId?: string
): FormAncestryRuntimeEntry[] => {
  if (!formInstanceId) return []

  const instances = selectAncestorInstances(
    store.getState() as FormAncestryRootState,
    formInstanceId
  )

  const entries: FormAncestryRuntimeEntry[] = []
  for (const instance of instances) {
    const getValues = getFormAncestryGetValues(instance.instanceId)
    if (!getValues) continue
    entries.push({
      entityName: instance.entityName,
      layoutUid: instance.layoutUid,
      getValues,
    })
  }
  return entries
}

export const findAncestorEntry = (
  ancestors: FormAncestryRuntimeEntry[],
  ref: { entity_name?: string; scope?: string }
): FormAncestryRuntimeEntry | undefined => {
  if (!ancestors.length) return undefined

  if (ref.scope === 'parent') {
    return ancestors[ancestors.length - 1]
  }

  const entityName = ref.entity_name?.trim().toLowerCase()
  if (!entityName) return undefined

  for (let i = ancestors.length - 1; i >= 0; i--) {
    if (ancestors[i].entityName.toLowerCase() === entityName) {
      return ancestors[i]
    }
  }

  return undefined
}

export const resolveAncestorFieldRef = (
  ancestors: FormAncestryRuntimeEntry[],
  ref: AncestorFieldRef
): unknown => {
  const keyName = ref.key_name?.trim()
  if (!keyName) return undefined

  const entry = findAncestorEntry(ancestors, ref)
  if (!entry) return undefined
  return resolveFieldRef(entry.getValues, keyName, ref.path ?? 'id')
}

export const resolveAncestorCacheRef = (
  ancestors: FormAncestryRuntimeEntry[],
  ref: AncestorCacheRef
): unknown => {
  const cacheKey = ref.cache_key?.trim()
  const path = ref.path?.trim()
  if (!cacheKey || !path) return undefined

  const entry = findAncestorEntry(ancestors, ref)
  if (!entry?.layoutUid) return undefined

  const cached = getRuleCache(entry.layoutUid, cacheKey)
  if (cached == null) return undefined

  return getValueByDotPath(cached, path)
}

export const ancestorFieldHasValue = (
  ancestors: FormAncestryRuntimeEntry[],
  entityName: string,
  keyName: string,
  path = 'id'
): boolean => {
  const value = resolveAncestorFieldRef(ancestors, {
    entity_name: entityName,
    key_name: keyName,
    path,
  })
  return value != null && value !== ''
}

export const parentFieldHasValue = (
  ancestors: FormAncestryRuntimeEntry[],
  keyName: string,
  path = 'id'
): boolean => {
  const value = resolveAncestorFieldRef(ancestors, {
    scope: 'parent',
    key_name: keyName,
    path,
  })
  return value != null && value !== ''
}
